<template>
  <div class="w-100">
    <WorkpackagesToolbar
      doc-path="190-bundles"
      :entity="entities.bundle"
      archived
      @on-search="onSearch"
    />
    <v-alert
      v-if="bundlesStore.loadingArchivedBundles && !bundlesStore.archivedBundlesTotalCount"
      border="start"
      border-color="primary-accent-4"
      color="primary-accent-4"
      variant="tonal"
    >
      <v-progress-circular indeterminate color="primary" size="20" />
      {{ $t('bundlesPage.loadingArchivedBundles') }}
    </v-alert>
    <v-card v-else class="col-box">
      <v-row no-gutters>
        <v-col class="workpackages-container colspan">
          <WorkpackagesList
            :entity="entities.bundle"
            :workpackages="bundlesStore.archivedBundles"
            :visible-workpackages="visibleBundles"
          />
          <v-alert
            v-if="!bundlesStore.loadingArchivedBundles && !visibleBundles.length"
            type="info"
            variant="tonal"
          >
            {{ $t(`bundlesPage.noArchived${capitalize(entities.bundle)}s`) }}
          </v-alert>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="horizontal-divider-container colspan">
          <div class="horizontal-divider" />
        </v-col>
      </v-row>

      <template v-if="bundlesStore.archivedBundlesTotalCount">
        <div class="text-center">
          <v-pagination
            v-model="currentPage"
            :length="totalPageCount"
            :total-visible="totalVisiblePages"
            variant="elevated"
            active-color="primary"
          />
        </div>
      </template>
      <v-overlay v-if="bundlesStore.loadingArchivedBundles" absolute opacity="0.5" scrim="white">
        <v-progress-circular indeterminate color="primary" size="64" width="10" />
      </v-overlay>
    </v-card>
  </div>
</template>

<script>
import { ceil, capitalize } from 'lodash';
import entities from '@enums/entities';
import searchUtils from '@/utils/search-utils';
import sortUtils from '@/utils/sort-utils';
import { useBundlesStore } from '@/stores/bundles';
import { useContextStore } from '@/stores/context';

export default {
  setup() {
    const bundlesStore = useBundlesStore();
    const contextStore = useContextStore();
    return { bundlesStore, contextStore };
  },

  data() {
    return {
      regexSearch: '',
      currentPage: 1,
      pageSize: null,
      totalVisiblePages: null,
      sortOrder: null,
      entities,
      capitalize,
      searchText: '',
    };
  },
  computed: {
    totalPageCount() {
      return ceil(this.bundlesStore.archivedBundlesTotalCount / this.pageSize);
    },

    offset() {
      return (this.currentPage - 1) * this.pageSize;
    },

    visibleBundles() {
      return searchUtils.getResultsByNameDescription(
        this.searchText,
        this.bundlesStore.archivedBundles,
        this.sortOrder
      );
    },

    sortedBundlesList() {
      return this.sortOrder
        ? sortUtils.sortItemList(this.bundlesStore.archivedBundles, this.sortOrder)
        : this.bundles;
    },
  },
  watch: {
    async currentPage(page) {
      this.setCurrentPage(page);
      await this.fetchArchivedBundlesPage({ limit: this.pageSize, page });
    },
  },
  async created() {
    const { pageSize, totalVisiblePages } = this.contextStore.clientConfig.archivedWorpackages;
    this.pageSize = pageSize;
    this.totalVisiblePages = totalVisiblePages;
    this.setCurrentPage(this.currentPage);
    await this.fetchArchivedBundlesPage({ limit: this.pageSize, page: this.currentPage });
  },

  methods: {
    // fetches archived bundles page (records for single pagination page)
    async fetchArchivedBundlesPage(params) {
      const { limit, filter } = params;
      await this.bundlesStore.fetchArchivedBundles({
        limit,
        offset: this.offset,
        filter,
        regexSearch: this.searchPhrase,
      });
    },

    async onSearch(searchText) {
      this.searchPhrase = searchText;
      await this.bundlesStore.fetchArchivedBundles({
        limit: this.pageSize,
        offset: this.offset,
        regexSearch: this.searchPhrase,
      });

      const numberOfPages = Math.floor(this.bundlesStore.archivedBundlesTotalCount / this.pageSize);
      // navigate to the first page if number of search results if less than expected for the current selected page
      if (numberOfPages <= this.currentPage && this.currentPage !== 1) {
        this.currentPage = 1;
      }
    },

    setCurrentPage(page) {
      this.bundlesStore.setArchivedBundlesCurrentPageMutation(page);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.workpackages-container {
  overflow-x: auto;
}

:deep(.v-application .headline) {
  font-family: $assortment-regular-font;
}

:deep(.progress-bar) {
  padding-bottom: 10em;
}
</style>
